import React, { useEffect, useState } from "react";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";

import { Link, useLocation, useParams } from "react-router-dom";
import { findObjectByKeyValue } from "../components/utils.js";

import { TeamPageNav } from "../components/team-page/TeamPageNav";
import { TeamDropdown } from "../components/common/Dropdown.jsx";
import { FieldPlayers } from "../components/team-page/FieldPlayers";
import { ListPlayers } from "../components/team-page/ListPlayers";
import { MobilePlayers } from "../components/team-page/MobilePlayers";
import { Spinner } from "../components/common/Spinner";
import { ErrorMessage } from "../components/common/ErrorMessage";
import { toast } from 'react-hot-toast';

import imgField from "../assets/field.svg";
import { useGetTeamById, useGetTeamsheet, useSaveTeamsheet } from "../api/teamsApi.js";
import { useGetTeamPlayers } from "../api/playersApi.js";
import { useActiveLeague } from "../hooks/useActiveLeague";
import { useCurrentTeamId } from "../hooks/useCurrentTeamId";
import { shareOrCopy } from "../utils/share";
import { buildPublicTeamLink } from "../utils/leagueLinks";
import { useQuickplayState } from "../api/quickplayApi.js";
import SquadReveal from "../components/cards/SquadReveal";
import {
  readSquadRevealSeen,
  shouldShowSquadReveal,
} from "../components/cards/squadRevealUtils";

// Import static JSON data
import formationsData from "../json-data/field-positions.json";
import tacticData from "../json-data/tactic.json";

// Import the new component
import SubstitutesSection from '../components/team-page/SubstitutesSection';
import SlotPickerDialog from '../components/team-page/SlotPickerDialog';
// Import ItemTypes from the new constants file
import { ItemTypes, MAX_BENCH_SLOT_COUNT } from '../components/team-page/Constants';
import { groupOf, normalizeCardPlayer } from '../components/cards/cardUtils.js';

// Same breakpoint the stylesheet uses for the mobile team page layout.
// Read at tap time rather than kept in state, so a resize or rotation
// crossing the breakpoint can never leave a stale value behind.
const isMobileViewport = () => typeof window !== 'undefined'
  && typeof window.matchMedia === 'function'
  && window.matchMedia('(max-width: 960px)').matches;
import { Popup } from "../components/common/Popup";
import TutorialTooltip from '../components/common/TutorialTooltip';

function createTeamsheetSnapshot({
  formationId,
  tacticId,
  playersOnField,
  subs,
}) {
  const players = playersOnField
    .filter(slot => slot.player_id !== null && slot.player_info !== null)
    .map(slot => ({
      slot: slot.id,
      position: slot.title,
      wyId: String(slot.player_id),
    }))
    .sort((a, b) => a.slot - b.slot);
  const substitutes = subs
    .map(sub => ({
      slot: sub.slot,
      position: sub.position || null,
      wyId: String(sub.playerInfo.wyId),
    }))
    .sort((a, b) => a.slot - b.slot);

  return JSON.stringify({
    formationId: formationId ?? null,
    tacticId: tacticId ?? null,
    players,
    substitutes,
  });
}

// Mirrors the backend squad generator's SUBSTITUTE_POSITION_BY_ROLE defaults,
// keyed by the position groups normalizeCardPlayer produces.
const SUBSTITUTE_POSITION_BY_GROUP = {
  GKP: 'GK',
  DEF: 'CB',
  MID: 'CM',
  FWD: 'ST',
};

function defaultSubPositionForPlayer(player) {
  const group = normalizeCardPlayer(player).positionGroup;
  return SUBSTITUTE_POSITION_BY_GROUP[group] || null;
}

function MyTeamPitch() {
  const { id: myTeamId, slug } = useParams();
  const location = useLocation();

  // Fetch team details
  const { 
    data: teamData, 
    isLoading: isTeamLoading, 
    error: teamError 
  } = useGetTeamById(myTeamId);
  
  const { activeLeague } = useActiveLeague();
  const currentTeamId = useCurrentTeamId();
  const [dismissedSquadRevealTeamId, setDismissedSquadRevealTeamId] = useState(null);

  // Quick Play managers land here before their match starts, so the page also
  // carries the intro message. The step bar itself lives in the header slot.
  // Normal leagues skip both and never call the Quick Play endpoint.
  const isQuickplayLeague = activeLeague?.gameMode === 'quickplay';
  const { data: quickplayState } = useQuickplayState(isQuickplayLeague);
  const quickplayActiveMatch = quickplayState?.activeMatch;
  const showQuickplayIntro = isQuickplayLeague && quickplayActiveMatch?.status === 'scheduled';

  // Fetch team players with league & season for stat enrichment
  const { 
    data: playersApiResponse, 
    isLoading: isPlayersLoading, 
    error: playersError 
  } = useGetTeamPlayers(
      myTeamId,
      { leagueId: activeLeague?._id, season: activeLeague?.season }
  );
  
  // Fetch team sheet
  const {
    data: teamSheet,
    isLoading: isTeamSheetLoading,
    error: teamSheetError
  } = useGetTeamsheet(myTeamId);
  
  // Combine loading states (removed isTeamsLoading)
  const isLoading = isTeamLoading || isPlayersLoading || isTeamSheetLoading;
  
  // Combine error states (removed teamsError)
  const hasError = teamError || playersError || teamSheetError;
  const errorMessage = hasError ? 
    (teamError?.message || playersError?.message || teamSheetError?.message) :
    "Error loading team data";
  
  // *** DEBUG LOGGING START ***
  console.log('[MyTeamPitch] Raw teamData from useGetTeamById:', teamData);
  console.log('[MyTeamPitch] Raw playersApiResponse from useGetTeamPlayers:', playersApiResponse);
  console.log('[MyTeamPitch] Raw teamSheet from useGetTeamsheet:', teamSheet);
  // *** DEBUG LOGGING END ***
  
  const processedPlayersData = playersApiResponse?.data || [];
  
  const [activeSettings, setActiveSettings] = React.useState(false);

  // Initialize directly from static import
  const [activeFormationId, setActiveFormationId] = React.useState(formationsData[0].id);
  const activeFormation = formationsData && activeFormationId
    ? findObjectByKeyValue(formationsData, "id", activeFormationId)
    : null;
    
  const [playersOnField, setPlayersOnField] = React.useState([]);
  const [activeTacticId, setActiveTacticId] = React.useState(tacticData?.[0]?.id || null);
  // Add state for substitutes
  const [subs, setSubs] = React.useState([]); // Shape: { slot: number, playerInfo: object, position: string | null }
  const [baselineSnapshot, setBaselineSnapshot] = React.useState(null);

  const revealStartingXi = React.useMemo(
    () => playersOnField
      .filter((slot) => slot.player_info)
      .map((slot) => ({
        ...slot.player_info,
        position: slot.title,
        slotPosition: slot.title,
      })),
    [playersOnField]
  );

  // Average Overall of the current starting XI; null until at least one
  // filled slot carries a numeric overall.
  const teamOverall = React.useMemo(() => {
    const values = playersOnField
      .map((slot) => slot.player_info?.overall)
      .filter((overall) => Number.isFinite(overall));
    if (values.length === 0) return null;
    return Math.round(values.reduce((sum, overall) => sum + overall, 0) / values.length);
  }, [playersOnField]);

  const currentTeamsheetSnapshot = React.useMemo(
    () => createTeamsheetSnapshot({
      formationId: activeFormationId,
      tacticId: activeTacticId,
      playersOnField,
      subs,
    }),
    [activeFormationId, activeTacticId, playersOnField, subs]
  );
  const isDirty = baselineSnapshot !== null
    && currentTeamsheetSnapshot !== baselineSnapshot;

  // Create a simple array of WYIDs for players on the bench for status checking
  const playerIdsOnBench = React.useMemo(() => 
    subs.map(sub => sub.playerInfo.wyId), 
    [subs]
  );

  // State for save operation
  const { 
    mutate: saveTeamsheetMutate, 
    isLoading: isSaving, 
    error: saveError 
  } = useSaveTeamsheet();

  const [isSuccessPopupOpen, setIsSuccessPopupOpen] = useState(false);

  // Initialize or update field positions when formation OR teamsheet OR player data changes
  useEffect(() => {
    console.log('[MyTeamPitch Effect] Running.'); // Log effect run
    const currentSheetData = teamSheet?.data;
    const playersReady = processedPlayersData.length > 0;
    const formationsReady = formationsData.length > 0;

    // Log the data availability status
    console.log(`[MyTeamPitch Effect] Data Status: currentSheetData: ${!!currentSheetData}, playersReady: ${playersReady}, formationsReady: ${formationsReady}`);

    // Only proceed if data is ready 
    if (currentSheetData && playersReady && formationsReady) {
        console.log('[MyTeamPitch Effect] Syncing state (All data ready):', currentSheetData);

        // --- Determine Target State from Sheet ---        
        let targetFormationId = activeFormationId; // Start with current state value (important for comparison later)
        let targetTacticId = activeTacticId;   // Start with current state value
        let targetFieldState = null;          // Will calculate if needed
        let formationToUseForField = null;    // Formation object used for setting field state

        // 1. Find Formation from Sheet
        const formationFromSheet = formationsData.find(f => f.title === currentSheetData.formation);
        if (formationFromSheet) {
            // Store the ID found in the sheet
            targetFormationId = formationFromSheet.id; 
            formationToUseForField = formationFromSheet; // Use this formation for player mapping
        } else {
            console.warn(`[MyTeamPitch Sync] Sheet formation "${currentSheetData.formation}" not found. Using current active ID: ${activeFormationId}`);
            // Fallback to using the currently active formation for player mapping if sheet one is invalid
            // Note: activeFormationId here might be stale in rapid re-renders, but we use it only as fallback
             formationToUseForField = formationsData.find(f => f.id === activeFormationId) || formationsData[0];
             // Keep targetFormationId as the current activeFormationId if sheet one is invalid
             targetFormationId = activeFormationId; 
        }

        // 2. Find Tactic ID from Sheet
        const tacticIdReverseMapping = { "A": 1, "N": 2, "D": 3, "C": 4, "P": 5, "L": 6 };
        const mappedTacticId = tacticIdReverseMapping[currentSheetData.tacticId];
        if (mappedTacticId) {
            // Store the tactic ID found/mapped from the sheet
            targetTacticId = mappedTacticId; 
        } else if (currentSheetData.tacticId) {
             console.warn(`[MyTeamPitch Sync] Tactic ID "${currentSheetData.tacticId}" from sheet could not be mapped.`);
             // Keep targetTacticId as the current activeTacticId if mapping fails
             targetTacticId = activeTacticId; 
        }

        // 3. Calculate Target Field State (only if formationToUseForField is valid)
        if (formationToUseForField && formationToUseForField.positions) {
             const sheetPlayers = currentSheetData.players || [];
             // Create a mutable copy to track used players from the sheet
             let availableSheetPlayers = [...sheetPlayers]; 

             // Initialize field state based on formation, initially empty
             targetFieldState = formationToUseForField.positions.map((pos, index) => ({
                 id: index,
                 title: pos.position || pos.title,
                 player_id: null,
                 player_info: null,
             }));

             // Now, populate the targetFieldState using players from the sheet
             targetFieldState = targetFieldState.map(slot => {
                // Find index of the first available player in the sheet matching this slot's title
                const matchingSheetPlayerIndex = availableSheetPlayers.findIndex(
                    p => p.position === slot.title
                );

                if (matchingSheetPlayerIndex !== -1) {
                    // Player found in the sheet for this position
                    const sheetPlayer = availableSheetPlayers[matchingSheetPlayerIndex];
                    const playerId = sheetPlayer.wyId;
                    let playerInfo = null;

                    if (playerId != null) {
                        // Find full player details from the main player list
                        playerInfo = processedPlayersData.find(p => String(p.wyId) === String(playerId));
                        if (!playerInfo) {
                            console.warn(`[MyTeamPitch Sync] Player WYID ${playerId} (Pos: ${slot.title}) from sheet not found in processedPlayersData`);
                        }
                    }

                    // Remove the player from the available list so they aren't used again
                    availableSheetPlayers.splice(matchingSheetPlayerIndex, 1);

                    // Return the updated slot data
                    return { 
                        ...slot, 
                        player_id: playerInfo ? playerId : null, // Assign ID only if playerInfo found
                        player_info: playerInfo 
                    };
                }
                
                // No matching player found in the sheet for this slot, return the empty slot
                return slot;
             });

             // Log if any players from the sheet weren't placed (e.g., position mismatch)
             if (availableSheetPlayers.length > 0) {
                 console.warn("[MyTeamPitch Sync] Some players from the saved teamsheet were not placed on the field:", availableSheetPlayers);
             }

        } else {
             console.warn('[MyTeamPitch Sync] Cannot calculate target field state because formation is invalid.');
        }

        const targetSubsState = (currentSheetData.subs || []).map((sub, index) => {
            const playerInfo = processedPlayersData.find(
                p => String(p.wyId) === String(sub.wyId)
            );
            if (!playerInfo) {
                console.warn(`[MyTeamPitch Effect - Subs] Could not find player info for sub wyId: ${sub.wyId}`);
                return null;
            }
            return {
                slot: index + 1,
                playerInfo,
                position: sub.position || defaultSubPositionForPlayer(playerInfo)
            };
        }).filter(Boolean);

        // --- Apply State Updates (only if changed) ---
        // Compare calculated target IDs/state with the CURRENT state values
        if (targetFormationId !== activeFormationId) {
             console.log(`[MyTeamPitch Effect - SETTING STATE] Calling setActiveFormationId(${targetFormationId})`); // Log before set
             setActiveFormationId(targetFormationId);
        }
         if (targetTacticId !== activeTacticId) {
             console.log(`[MyTeamPitch Effect - SETTING STATE] Calling setActiveTacticId(${targetTacticId})`); // Log before set
             setActiveTacticId(targetTacticId);
         }
        // Compare calculated targetFieldState with the CURRENT playersOnField state
        if (targetFieldState && JSON.stringify(targetFieldState) !== JSON.stringify(playersOnField)) {
            console.log('[MyTeamPitch Sync] Updating playersOnField state.');
            // *** ADDED LOGGING FOR HYPOTHESIS VERIFICATION ***
            console.log(`[Verify Hypothesis - useEffect] Setting playersOnField based on ${formationToUseForField?.title || 'unknown formation'}. IDs: ${JSON.stringify(targetFieldState.map(slot => slot.id))}`);
            // *** END ADDED LOGGING ***
            setPlayersOnField(targetFieldState);
            // *** ADDED LOGGING ***
            console.log('[MyTeamPitch Sync] setPlayersOnField called with:', targetFieldState);
        }
        if (JSON.stringify(targetSubsState) !== JSON.stringify(subs)) {
            console.log("[MyTeamPitch Effect - SETTING STATE] Setting initial subs state:", targetSubsState);
            setSubs(targetSubsState);
        }
        if (targetFieldState) {
            setBaselineSnapshot(createTeamsheetSnapshot({
                formationId: targetFormationId,
                tacticId: targetTacticId,
                playersOnField: targetFieldState,
                subs: targetSubsState,
            }));
        }

    } else {
        console.log('[MyTeamPitch Effect] Skipping sync: Missing sheet data, player data, or formations data.');
        // Potentially reset state here if needed when data disappears, e.g., setPlayersOnField([])
    }

}, [teamSheet, processedPlayersData, formationsData]); // Depend on the main objects

  const handleFormationChange = (id) => {
    setActiveFormationId(id);

    const newFormation = formationsData.find(f => f.id === id);
    if (!newFormation || !newFormation.positions) {
        setPlayersOnField([]); 
        return;
    }

    // Create the new field layout based on the new formation,
    // while preserving players in their original slot index.
    setPlayersOnField(currentField => {
      return newFormation.positions.map((pos, index) => {
          // Find the player who was in the corresponding slot (same index) in the old formation.
          const playerFromOldSlot = currentField[index]?.player_info || null;

          return {
              id: index,
              title: pos.position || pos.title,
              player_id: playerFromOldSlot ? playerFromOldSlot.wyId : null,
              player_info: playerFromOldSlot,
          };
      });
    });
  };

  const clearPlayerOnField = (playerId) => {
    setPlayersOnField((prevField) =>
      prevField.map((slot) =>
        slot.player_id === playerId
          ? { ...slot, player_id: null, player_info: null }
          : slot
      )
    );
      console.log('clear fix')

      if (touchedPlayer && touchedPlayer.wyId === playerId) {
          setTouchedPlayer(null);
      }
  };
  
  const [touchedPlayer, setTouchedPlayer] = React.useState(null);
  // Slot the mobile picker sheet is open for:
  // { type: 'field', slotId } | { type: 'bench', slotNumber } | null
  const [pickerSlot, setPickerSlot] = React.useState(null);

  const handleTouchedPlayer = (newlyTouchedItem) => {
    // --- Phase 1: Determine the nature of the items ---
    const newlyTouchedPlayer = newlyTouchedItem?.isEmptySlot ? null : newlyTouchedItem;
    const isTargetingEmptySlot = newlyTouchedItem?.isEmptySlot || false;
    const targetSlotId = newlyTouchedItem?.id;

    const previouslyTouchedOnField = touchedPlayer ? playersOnField.find(s => s.player_id === touchedPlayer.wyId) : null;
    const newlyTouchedOnField = newlyTouchedPlayer ? playersOnField.find(s => s.player_id === newlyTouchedPlayer.wyId) : null;
    
    const previouslyTouchedOnBench = touchedPlayer ? subs.find(s => s.playerInfo.wyId === touchedPlayer.wyId) : null;
    const newlyTouchedOnBench = newlyTouchedPlayer ? subs.find(s => s.playerInfo.wyId === newlyTouchedPlayer.wyId) : null;

    // --- Phase 2: Handle Actions ---

    // Action: Untoggle or cancel selection
    if (!newlyTouchedItem || (touchedPlayer && newlyTouchedPlayer?.wyId === touchedPlayer.wyId)) {
      setTouchedPlayer(null);
      return;
    }

    // Action: Swap two players on the FIELD
    if (previouslyTouchedOnField && newlyTouchedOnField) {
      setPlayersOnField(prevField => {
        const newField = [...prevField];
        const sourceIndex = newField.findIndex(s => s.id === previouslyTouchedOnField.id);
        const targetIndex = newField.findIndex(s => s.id === newlyTouchedOnField.id);
        if (sourceIndex === -1 || targetIndex === -1) return prevField; // Should not happen

        // Simple and safe swap of player info
        const sourcePlayerInfo = newField[sourceIndex].player_info;
        const targetPlayerInfo = newField[targetIndex].player_info;

          newField[sourceIndex] = {
              ...newField[sourceIndex],
              player_info: targetPlayerInfo,
              player_id: targetPlayerInfo?.wyId ?? null,
          };
          newField[targetIndex] = {
              ...newField[targetIndex],
              player_info: sourcePlayerInfo,
              player_id: sourcePlayerInfo?.wyId ?? null,
          };
        
        return newField;
      });
      setTouchedPlayer(null);
      return;
    }
    
    // Action: Swap a player from FIELD with a player from BENCH
    if ((previouslyTouchedOnField && newlyTouchedOnBench) || (previouslyTouchedOnBench && newlyTouchedOnField)) {
        const fieldPlayerSlot = previouslyTouchedOnField || newlyTouchedOnField;
        const benchPlayerSub = previouslyTouchedOnBench || newlyTouchedOnBench;

        setPlayersOnField(prevField => prevField.map(slot => 
            slot.id === fieldPlayerSlot.id ? { ...slot, player_id: benchPlayerSub.playerInfo.wyId, player_info: benchPlayerSub.playerInfo } : slot
        ));

        setSubs(prevSubs => prevSubs.map(sub => 
            sub.slot === benchPlayerSub.slot ? { ...sub, playerInfo: fieldPlayerSlot.player_info } : sub
        ));
        
        setTouchedPlayer(null);
        return;
    }

    // Action: A player is touched, and an empty FIELD slot is clicked
    if (touchedPlayer && isTargetingEmptySlot) {
      const targetSlot = playersOnField.find(s => s.id === targetSlotId);
      if (targetSlot && !targetSlot.player_id) {
        // This reuses the main drop logic, which is fine
        updatePlayerInfo(targetSlotId, touchedPlayer);
      }
      setTouchedPlayer(null);
      return;
    }

    // Action: Replace player on FIELD with player from LIST (not from bench)
    if (touchedPlayer && !previouslyTouchedOnField && !previouslyTouchedOnBench && newlyTouchedOnField) {
        updatePlayerInfo(newlyTouchedOnField.id, touchedPlayer); // Replaces player at target slot
        setTouchedPlayer(null);
        return;
    }

    // Default Action: Set the newly touched item as the selected player
    setTouchedPlayer(newlyTouchedPlayer);
  };
  
  // Function to handle dropping a player onto a substitute slot
  const handleSubDrop = (playerInfo, targetSlotNumber) => {
    if (!playerInfo) return;
    const draggedPlayerId = playerInfo.wyId;

    // Determine the source of the drag
    const sourceFieldSlot = playersOnField.find(s => s.player_id === draggedPlayerId);
    
    // Find the player occupying the target sub slot, if any
    const targetSub = subs.find(s => s.slot === targetSlotNumber);
    const playerInTargetSub = targetSub?.playerInfo;

    // --- State Update Logic ---
    let nextFieldState = [...playersOnField];
    let nextSubsState = [...subs];

    // Case 1: Dragging from the FIELD to the BENCH
    if (sourceFieldSlot) {
      // Find the field slot and replace it with the player from the target bench slot (or null if empty)
      const sourceFieldIndex = nextFieldState.findIndex(s => s.id === sourceFieldSlot.id);
      if (sourceFieldIndex !== -1) {
        nextFieldState[sourceFieldIndex] = {
          ...sourceFieldSlot,
          player_id: playerInTargetSub ? playerInTargetSub.wyId : null,
          player_info: playerInTargetSub || null,
        };
      }
      
      // Update subs: remove the player who was in the target slot and add the dragged player
      if (playerInTargetSub) {
        nextSubsState = nextSubsState.filter(s => s.playerInfo.wyId !== playerInTargetSub.wyId);
      }
      nextSubsState.push({
        slot: targetSlotNumber,
        playerInfo: playerInfo,
        position: targetSub?.position || sourceFieldSlot.title || defaultSubPositionForPlayer(playerInfo),
      });
    } else { // Case 2: Dragging from LIST or another BENCH slot
      // If the bench already holds the maximum and we're not swapping, prevent drop
      if (subs.length >= MAX_BENCH_SLOT_COUNT && !playerInTargetSub) {
        toast.error(`Bench is full (${MAX_BENCH_SLOT_COUNT} substitutes).`);
        return;
      }
      
      // Remove the dragged player from their old sub slot (if they were on the bench)
      const sourceSub = subs.find(s => s.playerInfo.wyId === draggedPlayerId);
      if (sourceSub) {
         nextSubsState = nextSubsState.filter(s => s.playerInfo.wyId !== draggedPlayerId);
      }

      // Handle the player being replaced (if any)
      if (playerInTargetSub) {
        // Remove the target player from the subs list
        nextSubsState = nextSubsState.filter(s => s.playerInfo.wyId !== playerInTargetSub.wyId);
        // If the drag was from another bench slot, move the target player to the source slot
        if (sourceSub) {
          nextSubsState.push({ ...targetSub, slot: sourceSub.slot });
        }
      }
      
      // Add the dragged player to the new slot
      nextSubsState.push({
        slot: targetSlotNumber,
        playerInfo: playerInfo,
        position: sourceSub?.position || targetSub?.position || defaultSubPositionForPlayer(playerInfo),
      });
    }

    // --- Commit State ---
    setPlayersOnField(nextFieldState);
    setSubs(nextSubsState.sort((a, b) => a.slot - b.slot));
    setTouchedPlayer(null);
  };

  // Handler for changing a substitute's position via dropdown
  const handleSubPositionChange = (slotNumber, newPosition) => {
    setSubs(prevSubs => {
        return prevSubs.map(sub => {
            if (sub.slot === slotNumber) {
                console.log(`[handleSubPositionChange] Updating slot ${slotNumber} to position ${newPosition}`);
                return { ...sub, position: newPosition };
            }
            return sub;
        });
    });
  };

  // Corrected logic to handle field-to-field swaps AND field-to-bench AND bench-to-field
  const updatePlayerInfo = (targetSlotId, draggedPlayerInfo) => {
    if (!draggedPlayerInfo) return;
    const draggedPlayerId = draggedPlayerInfo.wyId;
    const sourceSub = subs.find(sub => sub.playerInfo.wyId === draggedPlayerId);
    const targetFieldSlot = playersOnField.find(slot => slot.id === targetSlotId);
    const playerInTargetSlot = targetFieldSlot?.player_info || null;

    setSubs(prevSubs => {
      if (!sourceSub) {
        return prevSubs.filter(sub => sub.playerInfo.wyId !== draggedPlayerId);
      }

      if (!playerInTargetSlot) {
        return prevSubs.filter(sub => sub.slot !== sourceSub.slot);
      }

      return prevSubs.map(sub =>
        sub.slot === sourceSub.slot
          ? {
              ...sub,
              playerInfo: playerInTargetSlot,
              // The displaced starter keeps the field position they just left,
              // not the outgoing substitute's bench label.
              position: targetFieldSlot?.title || sub.position
            }
          : sub
      );
    });

    setPlayersOnField(prevField => {
      const newField = [...prevField];
      const targetSlotIndex = newField.findIndex(s => s.id === targetSlotId);
      const sourceSlotIndex = newField.findIndex(s => s.player_id === draggedPlayerId);

      if (targetSlotIndex === -1) return prevField; // Should not happen

      // Player that was in the target slot before the drop
      const currentPlayerInTargetSlot = newField[targetSlotIndex].player_info;

      // Place the dragged player into the target slot
      newField[targetSlotIndex] = {
        ...newField[targetSlotIndex],
        player_id: draggedPlayerId,
        player_info: draggedPlayerInfo,
      };

      // If the player came from another field slot (a field-to-field move)...
      if (sourceSlotIndex !== -1) {
        // ...put the player that was in the target slot into the source slot (swap),
        // or clear the source slot if the target was empty.
        newField[sourceSlotIndex] = {
          ...newField[sourceSlotIndex],
          player_id: currentPlayerInTargetSlot ? currentPlayerInTargetSlot.wyId : null,
          player_info: currentPlayerInTargetSlot || null,
        };
      }

      return newField;
    });

    setTouchedPlayer(null);
  };

  const handleSubstituteClick = (e, slotNumber) => {
    // Stop the click from bubbling up to any other listeners (like a window click listener)
    e.stopPropagation();

    // Find the player in the clicked substitute slot
    const subInSlot = subs.find(sub => sub.slot === slotNumber);
    const playerInSlot = subInSlot?.playerInfo;

    if (touchedPlayer) {
      // Case 1: A player is already touched/selected.
      // Try to drop the touched player into the sub slot.
      handleSubDrop(touchedPlayer, slotNumber);
      setTouchedPlayer(null); // Clear selection after the action.

    } else if (isMobileViewport()) {
      // Case 2 (mobile): open the picker sheet for this bench slot,
      // whether it is occupied or empty.
      setPickerSlot({ type: 'bench', slotNumber });
    } else if (playerInSlot) {
      // Case 3 (desktop): selecting a player from the bench.
      setTouchedPlayer(playerInSlot);
    }
    // Otherwise: no player touched and the slot is empty. Do nothing.
  };

  // Pitch slot tap. Mobile with nothing selected opens the picker sheet;
  // everything else (desktop, or the strip's select-then-place fast path)
  // keeps the original touch flow.
  const handleFieldSlotTap = (slotIndex) => {
    if (!touchedPlayer && isMobileViewport()) {
      setPickerSlot({ type: 'field', slotId: slotIndex });
      return;
    }
    const slotData = playersOnField.find(s => s.id === slotIndex);
    handleTouchedPlayer(slotData?.player_info ?? { isEmptySlot: true, id: slotIndex });
  };

  // Default position for a substitute added through the picker, so the
  // teamsheet stays saveable without a separate dropdown step: the position
  // the player just held (pitch slot, then their own bench role), else the
  // outgoing occupant's role, else the first formation position matching
  // the player's role group, else the stock bench position for that group.
  const defaultBenchPosition = (player, targetSub, sourceFieldSlot, sourceSub) => {
    if (sourceFieldSlot?.title) return sourceFieldSlot.title;
    if (sourceSub?.position) return sourceSub.position;
    if (targetSub?.position) return targetSub.position;
    const playerGroup = normalizeCardPlayer(player).positionGroup;
    const match = (activeFormation?.positions || []).find(
      pos => groupOf(pos.position || pos.title) === playerGroup
    );
    return match ? (match.position || match.title) : defaultSubPositionForPlayer(player);
  };

  const handlePickerPick = (player) => {
    if (!pickerSlot) return;
    if (pickerSlot.type === 'field') {
      updatePlayerInfo(pickerSlot.slotId, player);
    } else {
      const targetSub = subs.find(s => s.slot === pickerSlot.slotNumber);
      const sourceFieldSlot = playersOnField.find(s => s.player_id === player.wyId);
      const sourceSub = subs.find(s => s.playerInfo.wyId === player.wyId);
      const preferredPosition = defaultBenchPosition(player, targetSub, sourceFieldSlot, sourceSub);
      handleSubDrop(player, pickerSlot.slotNumber);
      if (preferredPosition) {
        handleSubPositionChange(pickerSlot.slotNumber, preferredPosition);
      }
    }
    setPickerSlot(null);
  };

  const handlePickerRemove = () => {
    if (!pickerSlot) return;
    if (pickerSlot.type === 'field') {
      const slotData = playersOnField.find(s => s.id === pickerSlot.slotId);
      if (slotData?.player_id) clearPlayerOnField(slotData.player_id);
    } else {
      const sub = subs.find(s => s.slot === pickerSlot.slotNumber);
      if (sub) handleRemoveSub(sub.playerInfo.wyId);
    }
    setPickerSlot(null);
  };

  // Handler to remove a substitute from the bench
  const handleRemoveSub = (playerIdToRemove) => {
    setSubs(prevSubs => {
      const updatedSubs = prevSubs.filter(sub => sub.playerInfo.wyId !== playerIdToRemove);
      console.log(`[handleRemoveSub] Removing player ${playerIdToRemove}. New subs:`, updatedSubs);
      // Re-assign slot numbers sequentially after removal (optional but good for consistency)
      return updatedSubs.map((sub, index) => ({ ...sub, slot: index + 1 }));
    });
    console.log('clear fix')
      if (touchedPlayer && touchedPlayer.wyId === playerIdToRemove) {
          setTouchedPlayer(null);
      }
    // Note: The player removed from the bench will automatically reappear in the ListPlayers 
    // component because the filter condition there will no longer exclude them.
  };

  // *** DEBUG LOGGING START ***
  console.log(`[MyTeamPitch] Checking empty state: processedPlayersData.length = ${processedPlayersData?.length}, myTeamId = ${myTeamId}`);
  // *** DEBUG LOGGING END ***
  
  const handleSaveTeamsheet = async () => {
    // No need to manually set isSaving/saveError here, the hook handles it
    console.log('[handleSaveTeamsheet] Starting save...');

    // 1. Gather data (Keep this part)
    const selectedFormation = findObjectByKeyValue(formationsData, "id", activeFormationId);
    const formationIdentifier = selectedFormation?.title;
    console.log('[handleSaveTeamsheet] Formation:', formationIdentifier);

    const selectedTactic = findObjectByKeyValue(tacticData, "id", activeTacticId);
    const tacticTitle = selectedTactic?.title;
    console.log('[handleSaveTeamsheet] Tactic:', tacticTitle);

    const tacticMapping = {
      "Attacking": "A",
      "Neutral": "N",
      "Defensive": "D",        
      "Counterattacking": "C",
      "Passing": "P",
      "Long Ball": "L",
    };
    const tacticIdForDb = tacticTitle ? tacticMapping[tacticTitle] : null;
    if (tacticTitle && tacticIdForDb === undefined) { 
      const errorMsg = `Tactic "${tacticTitle}" does not have a defined mapping for saving.`;
      console.error('[handleSaveTeamsheet] Tactic Mapping Error:', errorMsg);
      toast.error(errorMsg); 
      return; // Stop execution
    }
    console.log('[handleSaveTeamsheet] Tactic ID for DB:', tacticIdForDb);

    const playersForDb = playersOnField
      .filter(slot => slot.player_id !== null && slot.player_info !== null)
      .map(slot => ({
        position: slot.title,
        wyId: slot.player_id
      }));
    console.log('[handleSaveTeamsheet] Players for DB:', playersForDb);

    // Validate subs positions before saving
    const firstInvalidSub = subs.find(sub => !sub.position);
    if (firstInvalidSub) {
        const errorMsg = `Please select a position for substitute in slot ${firstInvalidSub.slot} (${firstInvalidSub.playerInfo.name}). It sets where they play if they come on, for example after an injury.`;
        console.error('[handleSaveTeamsheet] Validation Error:', errorMsg);
        toast.error(errorMsg);
        return; // Stop execution
    }

    if (!formationIdentifier || !tacticIdForDb || !myTeamId) {
      const errorMsg = "Missing required information (Formation, Tactic, or Team ID) to save.";
      console.error('[handleSaveTeamsheet] Validation Error:', errorMsg);
      toast.error(errorMsg); // Show validation error toast
      return; // Stop execution
    }

    // Modify handleSaveTeamsheet payload to use the new subs state structure
    const teamsheetPayload = {
      formation: formationIdentifier,
      tacticId: tacticIdForDb,
      players: playersForDb,
      // Map subs state, sending the actual selected position
      subs: subs.map(sub => ({ position: sub.position, wyId: sub.playerInfo.wyId })), 
    };
    console.log('[handleSaveTeamsheet] Payload:', teamsheetPayload);

    // 2. Call API using the mutation hook wrapped in toast.promise for unified feedback
    toast.promise(
      new Promise((resolve, reject) => {
        saveTeamsheetMutate(
          { teamId: myTeamId, teamsheetData: teamsheetPayload },
          {
            onSuccess: () => resolve(),
            onError: (error) => reject(error)
          }
        );
      }),
      {
        loading: 'Saving teamsheet…',
        success: 'Teamsheet saved successfully!',
        error: (err) => {
          const errorMsg = err?.response?.data?.message || err.message || 'Failed to save teamsheet.';
          return `Save failed: ${errorMsg}`;
        },
      }
    ).then(() => {
      setBaselineSnapshot(currentTeamsheetSnapshot);
      setIsSuccessPopupOpen(true);
    });

    console.log('[handleSaveTeamsheet] Save request sent.');
  };

  const handleShareTeam = () => {
    const leagueSlug = slug || activeLeague?.slug;
    const leagueName = activeLeague?.name || activeLeague?.title;
    shareOrCopy({
      title: teamData?.data?.name ? `${teamData.data.name} on Nova` : 'My team on Nova',
      text: leagueName ? `Check out my squad in ${leagueName} on Nova` : 'Check out my squad on Nova',
      url: buildPublicTeamLink(myTeamId, leagueSlug ? `/${leagueSlug}` : ''),
    });
  };

  // Render loading state FIRST
  if (isLoading) {
    return (
      <div className="page-index">
        <div className="wrapper">
          <TeamPageNav id={myTeamId} />
          <div className="loading-container">
            <Spinner />
            <p>Loading team data...</p>
          </div>
        </div>
      </div>
    );
  }

  // Render error state SECOND
  if (hasError) {
    return (
      <div className="page-index">
        <div className="wrapper">
          <TeamPageNav id={myTeamId} />
          <ErrorMessage message={errorMessage || "Error loading team data"} />
        </div>
      </div>
    );
  }

  // Log state value just before render return
  console.log(`[MyTeamPitch Render] activeTacticId = ${activeTacticId}, activeFormationId = ${activeFormationId}`);

  // Squad-creation flows (Quick Play start, team registration, invite join)
  // pass a squadReveal hint through navigation state because the league
  // gameMode and team ownership load through separate requests that land
  // after the team data. The hint lets the reveal show on the first render
  // instead of flashing the normal page; server data wins once it arrives.
  const squadRevealHint = location.state?.squadReveal;
  const hintIsForThisTeam = Boolean(squadRevealHint?.teamId)
    && String(squadRevealHint.teamId) === String(myTeamId);
  const isOwnTeam = (Boolean(currentTeamId)
    && String(currentTeamId) === String(myTeamId))
    || hintIsForThisTeam;
  const squadRevealSeen = readSquadRevealSeen(myTeamId)
    || dismissedSquadRevealTeamId === String(myTeamId);
  const showSquadReveal = shouldShowSquadReveal({
    gameMode: activeLeague?.gameMode
      ?? (hintIsForThisTeam ? squadRevealHint.gameMode : undefined),
    isOwnTeam,
    players: processedPlayersData,
    seen: squadRevealSeen,
  });

  return (
    <div className="page-index team-pitch-page">
      {showSquadReveal ? (
        <SquadReveal
          teamId={myTeamId}
          teamName={teamData?.data?.name}
          leagueName={activeLeague?.name || activeLeague?.title || activeLeague?.slug}
          players={processedPlayersData}
          startingXi={revealStartingXi}
          formationName={activeFormation?.title}
          onClose={() => setDismissedSquadRevealTeamId(String(myTeamId))}
        />
      ) : null}

      <Popup popOpen={isSuccessPopupOpen} close={() => setIsSuccessPopupOpen(false)}>
        <p style={{ padding: '20px', textAlign: 'center' }}>Team successfully saved</p>
      </Popup>

      {pickerSlot && (() => {
        const fieldSlot = pickerSlot.type === 'field'
          ? playersOnField.find(s => s.id === pickerSlot.slotId)
          : null;
        const benchSub = pickerSlot.type === 'bench'
          ? subs.find(s => s.slot === pickerSlot.slotNumber)
          : null;
        return (
          <SlotPickerDialog
            slot={{
              ...pickerSlot,
              position: pickerSlot.type === 'field'
                ? (fieldSlot?.title || null)
                : (benchSub?.position || null),
            }}
            currentPlayer={fieldSlot?.player_info || benchSub?.playerInfo || null}
            players={processedPlayersData}
            playersOnField={playersOnField}
            subs={subs}
            onPick={handlePickerPick}
            onRemove={handlePickerRemove}
            onClose={() => setPickerSlot(null)}
          />
        );
      })()}

      <DndProvider backend={HTML5Backend}>
        <div className="wrapper">
          {showQuickplayIntro && (
            <section className="quickplay-team-intro">
              <h2>This is your team</h2>
              <p>
                Welcome to Quick Play! We built this football team just for you, with real players. Have a look at your squad. When you are ready, click Match in the steps bar and your first game will start.
              </p>
            </section>
          )}

          <TeamPageNav id={myTeamId} />

          <div className="team-share">
            <div className="team-overall">
              <span className="team-overall__label">Team Overall</span>
              <span className="team-overall__value">{teamOverall ?? '-'}</span>
            </div>
            <button type="button" className="gold-cta" onClick={handleShareTeam}>
              SHARE MY TEAM
            </button>
          </div>

          <div className="field-grid">
            {/* Wrap settings, field, and players columns with DndProvider */}
            
            {/* Settings Column */} 
            <div
              className={`settings-col m-filters-col ${
                activeSettings ? "active" : ""
              }`}
            >
              <div className="m-filters-head">
                <h3>Tactics</h3>
                <button
                  className="filters-close-btn icon-s-cross"
                  onClick={() => {
                    setActiveSettings(false);
                  }}
                />
              </div>

              <div className="setting-block">
                <div className="grid-info-2">
                  <div className="col">
                    <h4>Team name</h4>
                    <div className="info">{teamData?.data?.name || 'Unknown Team'}</div>
                  </div>
                  <div className="col">
                    <h4>Managed by</h4>
                    <div className="info">
                      <Link to="#">{teamData?.data?.owner?.username || teamData?.data?.manager?.username || 'Unknown'}</Link>
                    </div>
                  </div>
                </div>
              </div>

              <div className="setting-block">
                <TutorialTooltip
                  id="tactics-tooltip"
                  title="Tactics"
                  description="Here you can set your formation and tactical mentality."
                  className="f-left"
                />
                <div className="drop-container">
                  <h4>Select formation</h4>
                  <TeamDropdown
                    props={formationsData || []}
                    setId={handleFormationChange}
                    activeItemId={activeFormationId}
                  />
                </div>
                <div className="drop-container">
                  <h4>Select tactic</h4>
                  {!teamSheet?.data ? (
                    <div className="info" style={{ padding: '10px 0' }}>Loading tactic...</div>
                  ) : (
                    <TeamDropdown
                      props={tacticData || []}
                      activeItemId={activeTacticId}
                      setId={setActiveTacticId}
                    />
                  )}
                </div>
              </div>

              <div className="setting-block">
                <div className="grid-info-2">
                  <div className="col">
                    <h4>Current position</h4>
                    <div className="info">{teamData?.data?.currentPosition || 'N/A'}</div>
                  </div>
                  <div className="col">
                    <h4>Overall rank</h4>
                    <div className="info">{teamData?.data?.overallRank || 'N/A'}</div>
                  </div>
                </div>
              </div>

              <Link to={slug ? `/${slug}/team-settings/${myTeamId}` : '#'} className="settings-submit-btn">
                TEAM SETTINGS
              </Link>

              {/* Visible on mobile too, so formation changes made in the
                  tactics drawer can be saved without leaving it. */}
              <div>
                <button
                  type="button"
                  className="settings-submit-btn"
                  onClick={handleSaveTeamsheet}
                  disabled={isSaving || !isDirty}
                  style={{ marginTop: '10px' }}
                >
                  {isSaving ? 'Saving...' : 'Save Teamsheet'}
                </button>
                {saveError && (
                  <div style={{ color: 'red', marginTop: '10px', fontSize: '0.9em' }}>
                    Error: {saveError?.response?.data?.message || saveError.message || 'An unknown error occurred'}
                  </div>
                )}
              </div>
              {/* Substitutes Section */}
              <div className="setting-block desktop-block">
                <SubstitutesSection 
                  subs={subs} 
                  onSubsChange={handleSubDrop} 
                  formationPositions={activeFormation?.positions || []} // Pass active formation positions
                  onPositionChange={handleSubPositionChange} // Pass the position handler
                  onRemoveSub={handleRemoveSub} // Pass the remove handler
                  onSubstituteClick={handleSubstituteClick}
                  color={teamData?.data?.kitColor}
                />
                <TutorialTooltip
                  id="substitutions-tooltip"
                  title="Substitutions"
                  description="You can pick up to 11 substitutes from the player list on the right side of the pitch. Simply drag the player you need onto the position on the subs bench."
                  className="f-left"
                />

              </div>
            </div> {/* End settings-col */} 

            {/* Field Column */} 
            <div className="field-col">
              <div className="field-block">
                <div className="filters-btn-block">
                  <button
                    type="button"
                    className="filters-btn"
                    onClick={() => {
                      setActiveSettings(true);
                    }}
                  >
                    <span className="ico icon-filters"></span>
                    TACTICS
                  </button>
                </div>
                <img src={imgField} alt="field" />
                <FieldPlayers
                  props={activeFormation}
                  activePlayers={playersOnField}
                  removePlayer={clearPlayerOnField}
                  touchPlayer={handleTouchedPlayer}
                  touchedPlayer={touchedPlayer}
                  updatePlayerInfo={updatePlayerInfo}
                  onSlotTap={handleFieldSlotTap}
                  isLoading={isTeamSheetLoading}
                  error={teamSheetError}
                />
              </div>
            </div> {/* End field-col */} 

            {/* Players Column */} 
            <div className="players-col">
              <TutorialTooltip
                id="team-players-tooltip"
                title="Player list"
                description="You can find all of your players here. Drag and drop players from here or from the bench onto the desired positions on the pitch."
                className="f-right"
              />

              <ListPlayers
                props={processedPlayersData}
                leagueSlug={slug}
                playersOnField={playersOnField} // Pass the full field array
                playerIdsOnBench={playerIdsOnBench} // Pass only IDs for the bench
                removePlayer={clearPlayerOnField} 
                touchPlayer={handleTouchedPlayer}
                touchedPlayer={touchedPlayer}
                updatePlayerInfo={updatePlayerInfo} 
                isLoading={isPlayersLoading} 
                error={playersError}
              />
            </div> {/* End players-col */} 

          </div> { /* End field-grid */ }
        </div> { /* End wrapper */ }

        <div className="mobile-blocks-container">

                <TutorialTooltip
                  id="substitutions-tooltip"
                  title="Substitutions"
                  description="You can pick up to 11 substitutes. Tap any spot on the pitch or the subs bench and pick the player you want there."
                  className="f-left"
                />
                <TutorialTooltip
                  id="tactics-tooltip"
                  title="Tactics"
                  description="Here you can set your formation and tactical mentality."
                  className="f-left"
                />

          <div className="wrapper">

            <SubstitutesSection
                subs={subs}
                onSubsChange={handleSubDrop}
                formationPositions={activeFormation?.positions || []} // Pass active formation positions
                onPositionChange={handleSubPositionChange} // Pass the position handler
                onRemoveSub={handleRemoveSub} // Pass the remove handler
                onSubstituteClick={handleSubstituteClick}
                color={teamData?.data?.kitColor}
            />
          </div>
              <div className="wrapper">
              <button 
                type="button" 
                className="settings-submit-btn"
                onClick={handleSaveTeamsheet}
                disabled={isSaving || !isDirty}
                style={{ marginTop: '10px', marginBottom: '20px' }}
              >
                {isSaving ? 'Saving...' : 'Save Teamsheet'}
              </button>
              {saveError && (
                <div style={{ color: 'red', marginTop: '10px', fontSize: '0.9em' }}>
                  Error: {saveError?.response?.data?.message || saveError.message || 'An unknown error occurred'}
                </div>
              )}
            </div>


            <MobilePlayers
                props={processedPlayersData}
                leagueSlug={slug}
                playersOnField={playersOnField} // Pass the full field array
                playerIdsOnBench={playerIdsOnBench} // Pass only IDs for the bench
                removePlayer={clearPlayerOnField}
                touchPlayer={handleTouchedPlayer}
                touchedPlayer={touchedPlayer}
                updatePlayerInfo={updatePlayerInfo}
                isLoading={isTeamLoading}
                error={teamError}
            />
        </div>
      </DndProvider>
    </div>
  );
}

export { MyTeamPitch, defaultSubPositionForPlayer };
